/**
 * `useThemeStore` — Zustand wrapper around the pure `ThemeEngine`.
 *
 * Owns the active `ThemeManifest` and exposes `apply(id)` to switch
 * themes at runtime (Requirement 8.4). The engine handles asset lazy-
 * loading and CSS custom-property writes; persistence is a fire-and-
 * forget callback that delegates to `usePreferencesStore.setActiveThemeId`
 * so the active theme survives a refresh (Requirement 8.7).
 *
 * Cross-slice dependency note:
 *   The engine reads `usePreferencesStore.getState().preferences` to
 *   discover the persisted theme id during `hydrate`, so the boot
 *   sequence MUST hydrate `usePreferencesStore` BEFORE calling
 *   `useThemeStore.hydrate()`. Calling them in the wrong order falls
 *   back to the seeded preferences default ('barbie'), which is safe
 *   but suppresses a previously-saved choice on the first render.
 *
 * The engine is constructed at module load time so `useThemeStore`
 * has a synchronous `current` value the moment the file is imported.
 * The persist callback is wired at construction; it does not actually
 * touch the persistence layer until `apply()` is called for the first
 * time, so this is safe even before the boot sequence has set the
 * active `ConversationStore`.
 *
 * Validates: Requirements 8.4, 8.7, 11.5.
 */

import { create } from 'zustand';

import { ALL_MANIFESTS } from '../themes/manifests';
import { createThemeEngine } from '../themes/registry';
import type { ThemeManifest } from '../themes/types';

import { usePreferencesStore } from './usePreferencesStore';

/** Default theme id when no preference is persisted yet (Requirement 8.1). */
const DEFAULT_THEME_ID = 'barbie';

/**
 * The shared engine instance. Constructed once at module load. The
 * persist callback delegates to `usePreferencesStore` so the active
 * theme id survives a refresh.
 *
 * Errors from the persistence layer are swallowed inside the engine
 * (the visual swap is non-blocking on persistence), so a thrown
 * `getActiveConversationStore()` during early boot will not crash
 * the apply pipeline — though in practice the boot sequence wires
 * the store before the first `apply()` is ever invoked.
 */
const engine = createThemeEngine({
  manifests: ALL_MANIFESTS,
  defaultId: DEFAULT_THEME_ID,
  persist: async (id: string): Promise<void> => {
    await usePreferencesStore.getState().setActiveThemeId(id);
  },
});

/** Public slice surface. */
export interface ThemeState {
  /** Currently active theme manifest. */
  current: ThemeManifest;
  /** `true` once `hydrate()` has resolved. */
  hydrated: boolean;
  /**
   * Read the active theme id from preferences and apply it. Falls back
   * to the default theme id when preferences have not been hydrated.
   */
  hydrate(): Promise<void>;
  /** Switch to the theme with the given id. */
  apply(id: string): Promise<void>;
  /** All registered manifests in registration order. */
  list(): readonly ThemeManifest[];
}

export const useThemeStore = create<ThemeState>((set) => ({
  current: engine.current(),
  hydrated: false,

  async hydrate(): Promise<void> {
    const prefs = usePreferencesStore.getState();
    // `preferences` is always defined (default seeded at slice init),
    // but if hydrate ran out of order the `activeThemeId` will be
    // 'barbie' rather than the persisted value. The boot sequence
    // hydrates preferences first to avoid that.
    let activeId = prefs.preferences.activeThemeId || DEFAULT_THEME_ID;
    // Guard against retired theme ids the user may have persisted in
    // a previous build (e.g. 'marine', 'sutlac', 'system'). When the
    // engine doesn't know the id, fall back to the default and rewrite
    // the preference so subsequent reads stay clean.
    if (engine.get(activeId) === null) {
      activeId = DEFAULT_THEME_ID;
      void prefs.setActiveThemeId(DEFAULT_THEME_ID);
    }
    // Skip apply() when the engine is already on the requested id —
    // avoids re-running asset thunks on every boot.
    if (engine.current().id !== activeId) {
      await engine.apply(activeId);
    }
    set({ current: engine.current(), hydrated: true });
  },

  async apply(id: string): Promise<void> {
    await engine.apply(id);
    set({ current: engine.current() });
  },

  list(): readonly ThemeManifest[] {
    return engine.list();
  },
}));

// Keep the slice in sync with engine-driven changes (e.g. an external
// caller that holds the engine reference and applies a theme without
// going through the slice). Returns an unsubscribe thunk that we
// intentionally drop — the engine instance lives for the lifetime of
// the page, and so does the subscription.
engine.onChange((manifest: ThemeManifest) => {
  useThemeStore.setState({ current: manifest });
});
